import {useEffect, useState} from 'react';
import {Phone} from 'lucide-react';
import {useIntl} from 'react-intl';
import type {EndedCall} from '../model/types';
import {CallAvatar} from '../components/CallAvatar';
import {formatCallDuration} from '../services/format';
import {callSurfaceMessages as m} from '../messages';

const ENDED_CARD_MS = 5_000;
const ENDED_GROUP_MS = 2_200;

interface EndedCardProps {
    readonly ended: EndedCall;
    readonly onClose: () => void;
    readonly onCallBack?: () => void;
}

export const EndedCard = ({ended, onClose, onCallBack}: EndedCardProps) => {
    const intl = useIntl();
    const name = ended.identity.name ?? ended.identity.number ?? intl.formatMessage(m.unknownCaller);
    const group = ended.kind === 'group';

    const [held, setHeld] = useState(false);
    const hold = {
        onPointerEnter: () => setHeld(true),
        onPointerLeave: () => setHeld(false),
        onFocus: () => setHeld(true),
        onBlur: () => setHeld(false),
    };

    useEffect(() => {
        if (held) {
            return;
        }
        const timer = window.setTimeout(onClose, group ? ENDED_GROUP_MS : ENDED_CARD_MS);
        return () => window.clearTimeout(timer);
    }, [group, held, onClose]);

    const subline = ended.remoteOutcome === 'declined'
        ? intl.formatMessage(m.callDeclined)
        : ended.remoteOutcome === 'busy'
            ? intl.formatMessage(m.callBusy)
            : ended.durationMs > 0
                ? intl.formatMessage(m.callEndedDuration, {duration: formatCallDuration(ended.durationMs)})
                : intl.formatMessage(m.callEnded);

    if (group) {
        return (
            <div className="phq-call-swap flex flex-col items-center gap-1 py-4 text-center" data-testid="call-ended" {...hold}>
                <p className="text-lg font-semibold leading-tight">{intl.formatMessage(m.callEnded)}</p>
                <p className="text-sm text-[var(--phq-call-ink-muted)]">{name}{ended.durationMs > 0 && ` · ${formatCallDuration(ended.durationMs)}`}</p>
            </div>
        );
    }

    return (
        <div className="phq-call-swap flex flex-col gap-3" data-testid="call-ended" {...hold}>
            <div className="flex flex-col items-center gap-2 pt-2 text-center">
                <CallAvatar identity={ended.identity} size={64}/>
                <p className="text-lg font-semibold leading-tight">{name}</p>
                <p className="text-sm font-medium text-[var(--phq-call-ink-muted)] tabular-nums">{subline}</p>
            </div>
            <div className="flex gap-2">
                {onCallBack && (
                    <button
                        type="button"
                        onClick={onCallBack}
                        className="phq-call-press inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-2xl bg-[var(--phq-call-brand)] text-[15px] font-semibold text-white"
                    >
                        <Phone size={18} aria-hidden="true"/>
                        {intl.formatMessage(m.callBack)}
                    </button>
                )}
                <button
                    type="button"
                    onClick={onClose}
                    className="phq-call-press inline-flex h-11 flex-1 items-center justify-center rounded-2xl bg-[var(--phq-call-surface-muted)] text-[15px] font-semibold"
                >
                    {intl.formatMessage(m.close)}
                </button>
            </div>
        </div>
    );
};
